import { useState, useEffect, useCallback, useMemo } from "react";
import api, { formatApiError, isAuthError } from "@/utils/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, Trash2, Receipt, Filter, X } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { isAdmin as isAdminRole } from "@/utils/roles";
import ExportButtons from "@/components/ExportButtons";
import useDocumentTitle from "@/hooks/useDocumentTitle";

const CATEGORIES = ["Trainer fee", "Venue", "Materials", "Travel", "Equipment", "Other"];

const categoryBadge = {
  "Trainer fee": "bg-sky-100 text-sky-700 border-sky-200",
  "Venue": "bg-purple-100 text-purple-700 border-purple-200",
  "Materials": "bg-emerald-100 text-emerald-700 border-emerald-200",
  "Travel": "bg-amber-100 text-amber-700 border-amber-200",
  "Equipment": "bg-blue-100 text-blue-700 border-blue-200",
  "Other": "bg-slate-100 text-slate-700 border-slate-200",
};

const todayISO = () => new Date().toISOString().slice(0, 10);
const fmt = (v) => `£${(Number(v) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function ExpensesPage() {
  useDocumentTitle("Expenses", "Track course costs and overhead spend.");
  const { user } = useAuth();
  const isAdmin = isAdminRole(user?.role);
  // Staff can view expenses (read-only). Only admin/superadmin can create,
  // edit, delete or email records.
  const canEdit = isAdmin;
  const canView = isAdmin || user?.role === "staff";

  const [expenses, setExpenses] = useState([]);
  const [summary, setSummary] = useState(null);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);

  // filters
  const [fCategory, setFCategory] = useState("all");
  const [fCourse, setFCourse] = useState("all");
  const [fMonth, setFMonth] = useState("");
  const [fPaid, setFPaid] = useState("all");
  const [search, setSearch] = useState("");

  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const blankForm = {
    course_id: "",
    category: "Other",
    description: "",
    amount_net: 0,
    vat_applicable: false,
    date_incurred: todayISO(),
    paid: false,
    notes: "",
  };
  const [form, setForm] = useState(blankForm);
  const [busy, setBusy] = useState(false);

  const buildQuery = useCallback(() => {
    const params = new URLSearchParams();
    if (fCategory !== "all") params.set("category", fCategory);
    if (fCourse !== "all") params.set("course_id", fCourse);
    if (fMonth) params.set("month", fMonth);
    if (fPaid !== "all") params.set("paid", fPaid);
    const s = params.toString();
    return s ? `?${s}` : "";
  }, [fCategory, fCourse, fMonth, fPaid]);

  const fetchData = useCallback(async () => {
    if (!canView) { setLoading(false); return; }
    try {
      const [{ data }, { data: s }] = await Promise.all([
        api.get(`/expenses${buildQuery()}`),
        api.get(`/expenses/summary`),
      ]);
      setExpenses(data.expenses || []);
      setSummary(s);
    } catch (err) {
      if (!isAuthError(err)) toast.error("Failed to load expenses");
    } finally {
      setLoading(false);
    }
  }, [buildQuery, canView]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // load courses once for selectors
  useEffect(() => {
    if (!canView) return;
    api.get("/courses?limit=500")
      .then(({ data }) => setCourses(data.courses || []))
      .catch(() => {});
  }, [canView]);

  const filteredExpenses = useMemo(() => {
    if (!search.trim()) return expenses;
    const q = search.trim().toLowerCase();
    return expenses.filter(e =>
      (e.description || "").toLowerCase().includes(q) ||
      (e.notes || "").toLowerCase().includes(q) ||
      (e.course_title || "").toLowerCase().includes(q) ||
      (e.category || "").toLowerCase().includes(q)
    );
  }, [expenses, search]);

  const computeTotal = (net, vat) => {
    const n = parseFloat(net) || 0;
    const v = vat ? n * 0.20 : 0;
    return { vat: v, total: n + v };
  };

  const openCreate = () => {
    setForm({ ...blankForm, date_incurred: todayISO() });
    setCreateOpen(true);
  };

  const openEdit = (e) => {
    setEditing(e);
    setForm({
      course_id: e.course_id || "",
      category: e.category || "Other",
      description: e.description || "",
      amount_net: e.amount_net || 0,
      vat_applicable: !!e.vat_applicable,
      date_incurred: e.date_incurred || todayISO(),
      paid: !!e.paid,
      notes: e.notes || "",
    });
    setEditOpen(true);
  };

  const submitCreate = async (ev) => {
    ev.preventDefault();
    if (!form.category || !form.amount_net) {
      toast.error("Category and amount are required");
      return;
    }
    setBusy(true);
    try {
      const body = {
        ...form,
        course_id: form.course_id === "none" ? "" : form.course_id,
        amount_net: parseFloat(form.amount_net) || 0,
      };
      await api.post("/expenses", body);
      toast.success("Expense added");
      setCreateOpen(false);
      fetchData();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const submitEdit = async (ev) => {
    ev.preventDefault();
    if (!editing) return;
    setBusy(true);
    try {
      const body = {
        ...form,
        course_id: form.course_id === "none" ? "" : form.course_id,
        amount_net: parseFloat(form.amount_net) || 0,
      };
      await api.put(`/expenses/${editing.id}`, body);
      toast.success("Expense updated");
      setEditOpen(false);
      setEditing(null);
      fetchData();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (e) => {
    if (!window.confirm(`Delete this expense (${fmt(e.total)})?`)) return;
    try {
      await api.delete(`/expenses/${e.id}`);
      toast.success("Expense deleted");
      fetchData();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const togglePaid = async (e) => {
    try {
      await api.put(`/expenses/${e.id}`, { paid: !e.paid });
      fetchData();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const clearFilters = () => {
    setFCategory("all");
    setFCourse("all");
    setFMonth("");
    setFPaid("all");
    setSearch("");
  };

  const totals = computeTotal(form.amount_net, form.vat_applicable);
  const activeFilterCount = (fCategory !== "all" ? 1 : 0) + (fCourse !== "all" ? 1 : 0) + (fMonth ? 1 : 0) + (fPaid !== "all" ? 1 : 0);

  if (loading) return <div className="animate-pulse text-muted-foreground" data-testid="expenses-loading">Loading expenses...</div>;
  if (!canView) return (
    <div className="border border-border rounded-md bg-white p-10 text-center" data-testid="expenses-restricted">
      <Receipt size={32} className="mx-auto text-slate-300 mb-3" />
      <h2 className="font-heading font-bold text-lg mb-1">Restricted</h2>
      <p className="text-sm text-muted-foreground">Expenses are restricted to staff and administrators.</p>
    </div>
  );

  return (
    <div className="space-y-5" data-testid="expenses-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading font-bold text-2xl tracking-tight">Expenses</h1>
          <p className="text-sm text-muted-foreground">Track course costs and overhead spending</p>
        </div>
        <div className="flex items-center gap-2">
          <ExportButtons endpoint="expenses" label="Expenses" />
          {canEdit && (
            <Button size="sm" onClick={openCreate} className="gap-1" data-testid="add-expense-btn">
              <Plus size={15} /> Add Expense
            </Button>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="expense-summary">
        <div className="border border-border rounded-md p-3 bg-white">
          <p className="text-[11px] text-muted-foreground uppercase tracking-wide mb-1">Total Spend</p>
          <p className="font-heading font-bold text-xl text-red-600">{fmt(summary?.total)}</p>
        </div>
        <div className="border border-border rounded-md p-3 bg-white">
          <p className="text-[11px] text-muted-foreground uppercase tracking-wide mb-1">Paid</p>
          <p className="font-heading font-bold text-xl text-emerald-600">{fmt(summary?.paid_total)}</p>
        </div>
        <div className="border border-border rounded-md p-3 bg-white">
          <p className="text-[11px] text-muted-foreground uppercase tracking-wide mb-1">Outstanding</p>
          <p className="font-heading font-bold text-xl text-amber-600">{fmt(summary?.unpaid_total)}</p>
        </div>
        <div className="border border-border rounded-md p-3 bg-white">
          <p className="text-[11px] text-muted-foreground uppercase tracking-wide mb-1">Records</p>
          <p className="font-heading font-bold text-xl">{summary?.count || 0}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="border border-border rounded-md p-3 bg-white space-y-3" data-testid="expense-filters">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            <Filter size={13} /> Filters
            {activeFilterCount > 0 && <Badge variant="secondary" className="ml-1 text-[10px]">{activeFilterCount}</Badge>}
          </div>

          <Select value={fCategory} onValueChange={setFCategory}>
            <SelectTrigger className="h-8 w-[160px] text-xs" data-testid="filter-category"><SelectValue placeholder="Category" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All categories</SelectItem>
              {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>

          <Select value={fCourse} onValueChange={setFCourse}>
            <SelectTrigger className="h-8 w-[220px] text-xs" data-testid="filter-course"><SelectValue placeholder="Course" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All courses</SelectItem>
              {courses.map(c => <SelectItem key={c.id} value={c.id}>{c.title} ({c.start_date})</SelectItem>)}
            </SelectContent>
          </Select>

          <Input
            type="month"
            value={fMonth}
            onChange={(e) => setFMonth(e.target.value)}
            className="h-8 w-[140px] text-xs"
            data-testid="filter-month"
            placeholder="Month"
          />

          <Select value={fPaid} onValueChange={setFPaid}>
            <SelectTrigger className="h-8 w-[120px] text-xs" data-testid="filter-paid"><SelectValue placeholder="Paid?" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Paid + Unpaid</SelectItem>
              <SelectItem value="true">Paid only</SelectItem>
              <SelectItem value="false">Unpaid only</SelectItem>
            </SelectContent>
          </Select>

          <Input
            placeholder="Search description, notes…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-8 text-xs flex-1 min-w-[160px]"
            data-testid="filter-search"
          />

          {activeFilterCount > 0 && (
            <Button size="sm" variant="ghost" onClick={clearFilters} className="h-8 text-xs gap-1" data-testid="clear-filters-btn">
              <X size={13} /> Clear
            </Button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="border border-border rounded-md bg-white overflow-hidden" data-testid="expenses-table">
        {filteredExpenses.length === 0 ? (
          <div className="p-10 text-center">
            <Receipt size={32} className="mx-auto text-slate-300 mb-3" />
            <p className="text-sm text-muted-foreground">No expenses found. {canEdit && "Click 'Add Expense' to record one."}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-border">
                <tr className="text-left text-xs text-muted-foreground">
                  <th className="px-3 py-2 font-medium">Date</th>
                  <th className="px-3 py-2 font-medium">Category</th>
                  <th className="px-3 py-2 font-medium">Description</th>
                  <th className="px-3 py-2 font-medium">Course</th>
                  <th className="px-3 py-2 font-medium text-right">Net</th>
                  <th className="px-3 py-2 font-medium text-right">VAT</th>
                  <th className="px-3 py-2 font-medium text-right">Total</th>
                  <th className="px-3 py-2 font-medium text-center">Paid</th>
                  {canEdit && <th className="px-3 py-2 font-medium text-right">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {filteredExpenses.map((e) => (
                  <tr key={e.id} className="border-b border-border last:border-0 hover:bg-slate-50" data-testid={`expense-row-${e.id}`}>
                    <td className="px-3 py-2 whitespace-nowrap text-xs text-slate-600">{e.date_incurred}</td>
                    <td className="px-3 py-2">
                      <Badge variant="outline" className={`text-[10px] ${categoryBadge[e.category] || categoryBadge.Other}`}>{e.category}</Badge>
                    </td>
                    <td className="px-3 py-2 max-w-[260px]">
                      <p className="font-medium text-foreground truncate">{e.description || <span className="text-muted-foreground italic">—</span>}</p>
                      {e.notes && <p className="text-[11px] text-muted-foreground truncate">{e.notes}</p>}
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-600 max-w-[200px] truncate">
                      {e.course_title || <span className="text-muted-foreground italic">—</span>}
                    </td>
                    <td className="px-3 py-2 text-right text-xs">{fmt(e.amount_net)}</td>
                    <td className="px-3 py-2 text-right text-xs text-muted-foreground">{fmt(e.vat_amount)}</td>
                    <td className="px-3 py-2 text-right font-semibold">{fmt(e.total)}</td>
                    <td className="px-3 py-2 text-center">
                      <button
                        onClick={() => canEdit && togglePaid(e)}
                        disabled={!canEdit}
                        className="cursor-pointer disabled:cursor-not-allowed"
                        data-testid={`toggle-paid-${e.id}`}
                      >
                        <Badge variant="outline" className={e.paid ? "bg-emerald-100 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-600 border-slate-200"}>
                          {e.paid ? "Paid" : "Unpaid"}
                        </Badge>
                      </button>
                    </td>
                    {canEdit && (
                      <td className="px-3 py-2 text-right">
                        <div className="inline-flex gap-1">
                          <Button size="sm" variant="ghost" onClick={() => openEdit(e)} className="h-7 w-7 p-0" data-testid={`edit-expense-${e.id}`}>
                            <Pencil size={13} />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => handleDelete(e)} className="h-7 w-7 p-0 text-red-600 hover:bg-red-50" data-testid={`delete-expense-${e.id}`}>
                            <Trash2 size={13} />
                          </Button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Category breakdown */}
      {summary?.by_category && Object.keys(summary.by_category).length > 0 && (
        <div className="border border-border rounded-md p-4 bg-white" data-testid="expense-by-category">
          <h3 className="font-heading font-semibold text-sm mb-3">Spend by Category</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
            {Object.entries(summary.by_category).sort((a, b) => b[1] - a[1]).map(([cat, amt]) => (
              <div key={cat} className="border border-border rounded-md p-2.5">
                <Badge variant="outline" className={`text-[10px] mb-1 ${categoryBadge[cat] || categoryBadge.Other}`}>{cat}</Badge>
                <p className="font-heading font-bold text-sm">{fmt(amt)}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Create Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-lg" data-testid="create-expense-dialog">
          <DialogHeader><DialogTitle>Add Expense</DialogTitle></DialogHeader>
          <ExpenseForm form={form} setForm={setForm} courses={courses} totals={totals} onSubmit={submitCreate} busy={busy} submitLabel="Create" />
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editOpen} onOpenChange={(o) => { setEditOpen(o); if (!o) setEditing(null); }}>
        <DialogContent className="max-w-lg" data-testid="edit-expense-dialog">
          <DialogHeader><DialogTitle>Edit Expense</DialogTitle></DialogHeader>
          <ExpenseForm form={form} setForm={setForm} courses={courses} totals={totals} onSubmit={submitEdit} busy={busy} submitLabel="Save Changes" />
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ExpenseForm({ form, setForm, courses, totals, onSubmit, busy, submitLabel }) {
  return (
    <form onSubmit={onSubmit} className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs">Date *</Label>
          <Input
            type="date"
            value={form.date_incurred}
            onChange={(e) => setForm(f => ({ ...f, date_incurred: e.target.value }))}
            className="h-9 text-sm"
            required
            data-testid="form-date"
          />
        </div>
        <div>
          <Label className="text-xs">Category *</Label>
          <Select value={form.category} onValueChange={(v) => setForm(f => ({ ...f, category: v }))}>
            <SelectTrigger className="h-9 text-sm" data-testid="form-category"><SelectValue /></SelectTrigger>
            <SelectContent>
              {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div>
        <Label className="text-xs">Description</Label>
        <Input
          value={form.description}
          onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))}
          placeholder="e.g. Venue hire — Crawley training centre"
          className="h-9 text-sm"
          data-testid="form-description"
        />
      </div>

      <div>
        <Label className="text-xs">Link to Course (optional)</Label>
        <Select value={form.course_id || "none"} onValueChange={(v) => setForm(f => ({ ...f, course_id: v === "none" ? "" : v }))}>
          <SelectTrigger className="h-9 text-sm" data-testid="form-course"><SelectValue placeholder="No course" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="none">— Overhead / Not linked —</SelectItem>
            {courses.map(c => <SelectItem key={c.id} value={c.id}>{c.title} ({c.start_date})</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-2 gap-3 items-end">
        <div>
          <Label className="text-xs">Amount (Net £) *</Label>
          <Input
            type="number"
            step="0.01"
            min="0"
            value={form.amount_net}
            onChange={(e) => setForm(f => ({ ...f, amount_net: e.target.value }))}
            className="h-9 text-sm"
            required
            data-testid="form-amount-net"
          />
        </div>
        <div className="flex items-center justify-between p-2 border border-border rounded-md h-9">
          <Label className="text-xs cursor-pointer">VAT 20%</Label>
          <Switch
            checked={form.vat_applicable}
            onCheckedChange={(v) => setForm(f => ({ ...f, vat_applicable: v }))}
            data-testid="form-vat-toggle"
          />
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2 p-2 bg-slate-50 rounded-md text-xs">
        <div><span className="text-muted-foreground">Net: </span><span className="font-semibold">{`£${(parseFloat(form.amount_net)||0).toFixed(2)}`}</span></div>
        <div><span className="text-muted-foreground">VAT: </span><span className="font-semibold">{`£${totals.vat.toFixed(2)}`}</span></div>
        <div><span className="text-muted-foreground">Total: </span><span className="font-semibold text-red-600">{`£${totals.total.toFixed(2)}`}</span></div>
      </div>

      <div>
        <Label className="text-xs">Notes</Label>
        <Textarea
          value={form.notes}
          onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))}
          rows={2}
          className="text-sm"
          data-testid="form-notes"
        />
      </div>

      <div className="flex items-center gap-2 p-2 border border-border rounded-md">
        <Switch
          checked={form.paid}
          onCheckedChange={(v) => setForm(f => ({ ...f, paid: v }))}
          data-testid="form-paid-toggle"
        />
        <Label className="text-xs cursor-pointer">Mark as paid</Label>
      </div>

      <DialogFooter>
        <Button type="submit" disabled={busy} data-testid="form-submit-btn">{busy ? "Saving..." : submitLabel}</Button>
      </DialogFooter>
    </form>
  );
}
